import { useTranslate, useCustom } from '@refinedev/core';
import { Typography, Divider, Button, Alert, Space, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import xml from 'react-syntax-highlighter/dist/esm/languages/hljs/xml';
import { githubGist } from 'react-syntax-highlighter/dist/esm/styles/hljs';

SyntaxHighlighter.registerLanguage('xml', xml);

const { Title } = Typography;

const codeStyle = {
  maxHeight: 500,
  fontSize: 12,
  borderRadius: 4,
};

const formatOnixData = (data: unknown): string =>
  typeof data === 'string' ? data : JSON.stringify(data, null, 2);

export const ProductOnixTab = ({ productId }: { productId: number }) => {
  const t = useTranslate();

  const { query: normalizedQuery } = useCustom({
    url: `content_catalog/products/${productId}/onix/normalized`,
    method: 'get',
    queryOptions: { enabled: false },
  });

  const { query: rawQuery } = useCustom({
    url: `content_catalog/products/${productId}/onix/raw`,
    method: 'get',
    queryOptions: { enabled: false },
  });

  return (
    <>
      <Title level={4}>{t('content_catalog.products.onix.normalized')}</Title>
      {normalizedQuery.data == null ? (
        <Space direction="vertical">
          <Button
            onClick={() => normalizedQuery.refetch()}
            loading={normalizedQuery.isLoading}
          >
            {t('content_catalog.products.onix.load')}
          </Button>
          {normalizedQuery.isError && (
            <Alert
              type="error"
              message={normalizedQuery.error?.message ?? 'Error'}
            />
          )}
        </Space>
      ) : (
        <>
          <Button
            icon={<CopyOutlined />}
            size="small"
            style={{ marginBottom: 8 }}
            onClick={() => {
              navigator.clipboard.writeText(
                formatOnixData(normalizedQuery.data!.data)
              );
              message.success(t('common.copied'));
            }}
          >
            {t('common.copy')}
          </Button>
          <SyntaxHighlighter
            language="xml"
            style={githubGist}
            customStyle={codeStyle}
            wrapLongLines
          >
            {formatOnixData(normalizedQuery.data.data)}
          </SyntaxHighlighter>
        </>
      )}

      <Divider />

      <Title level={4}>{t('content_catalog.products.onix.raw')}</Title>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
        {t('content_catalog.products.onix.raw_hint')}
      </Typography.Paragraph>
      {rawQuery.data == null ? (
        <Space direction="vertical">
          <Button
            onClick={() => rawQuery.refetch()}
            loading={rawQuery.isLoading}
          >
            {t('content_catalog.products.onix.load')}
          </Button>
          {rawQuery.isError && (
            <Alert type="error" message={rawQuery.error?.message ?? 'Error'} />
          )}
        </Space>
      ) : (
        <>
          <Button
            icon={<CopyOutlined />}
            size="small"
            style={{ marginBottom: 8 }}
            onClick={() => {
              navigator.clipboard.writeText(
                formatOnixData(rawQuery.data!.data)
              );
              message.success(t('common.copied'));
            }}
          >
            {t('common.copy')}
          </Button>
          <SyntaxHighlighter
            language="xml"
            style={githubGist}
            customStyle={codeStyle}
            wrapLongLines
          >
            {formatOnixData(rawQuery.data.data)}
          </SyntaxHighlighter>
        </>
      )}
    </>
  );
};
